/**
 * Headland-turn detection for the "Remove turns" track filter.
 *
 * A port of backend-ai `src/lib/harvestGates.ts` turnRadiiMeters — the rule
 * analytics uses to keep headland manoeuvring out of harvested area. Keep the
 * two in step, so the map hides the same turns the numbers ignore.
 *
 * A working pass is straight or gently curved; a headland turn is not. Heading
 * between neighbouring fixes is mostly GPS noise (~1.5 m steps), so the radius
 * is taken over a 10 m chord either side of each fix instead.
 */

/** Chord either side of a fix over which its turn radius is measured, metres. */
export const TURN_CHORD_METERS = 10;

/** The analytics default for `min_turn_radius_m` when a device carries none. */
export const DEFAULT_MIN_TURN_RADIUS_M = 8;

/**
 * Most fixes walked either way to reach a full chord. A parked machine piles
 * hundreds of fixes inside 10 m, and walking all of them for every fix is
 * quadratic; past this the radius is "unknown", which keeps the fix (the speed
 * filter is what removes a parked machine).
 */
const MAX_WALK = 200;

const M_PER_DEGREE = 111194.93;
const EARTH_RADIUS_M = 6371008.8;

function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad;
  const dLon = (lon2 - lon1) * toRad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Turn radius at each fix, metres. `null` = unknown (too little stream either
 * side to span a chord) and must be treated as "not turning"; `Infinity` =
 * dead straight. `fixes` are [lng, lat] in time order.
 */
export function turnRadiiMeters(
  fixes: ReadonlyArray<readonly [number, number]>,
  chordMeters: number = TURN_CHORD_METERS
): Array<number | null> {
  const n = fixes.length;
  const out: Array<number | null> = new Array(n).fill(null);
  if (n < 3) return out;

  const straight = (a: number, b: number) =>
    haversineMeters(fixes[a][1], fixes[a][0], fixes[b][1], fixes[b][0]);

  for (let i = 0; i < n; i++) {
    let a = i;
    while (a > 0 && i - a < MAX_WALK && straight(a, i) < chordMeters) a--;
    if (straight(a, i) < chordMeters) continue;

    let b = i;
    while (b < n - 1 && b - i < MAX_WALK && straight(i, b) < chordMeters) b++;
    if (straight(i, b) < chordMeters) continue;

    // Circumradius of the triangle (a, i, b), in a local flat frame about i.
    const cosLat = Math.cos((fixes[i][1] * Math.PI) / 180);
    const ax = (fixes[a][0] - fixes[i][0]) * cosLat * M_PER_DEGREE;
    const ay = (fixes[a][1] - fixes[i][1]) * M_PER_DEGREE;
    const bx = (fixes[b][0] - fixes[i][0]) * cosLat * M_PER_DEGREE;
    const by = (fixes[b][1] - fixes[i][1]) * M_PER_DEGREE;

    const sideA = Math.hypot(ax, ay);
    const sideB = Math.hypot(bx, by);
    const sideC = Math.hypot(bx - ax, by - ay);
    const cross = Math.abs(ax * by - ay * bx);
    out[i] = cross < 1e-9 ? Infinity : (sideA * sideB * sideC) / (2 * cross);
  }
  return out;
}
